<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';
import { useReviewHistory } from '@/composables/useReviewHistory';
import { useToast } from 'primevue/usetoast';
import axios from 'axios';
import Divider from 'primevue/divider';
import SiteFeaturesInfo from '@/components/SiteFeaturesInfo.vue';
import ReviewHistoryDialog from '@/components/shared/ReviewHistoryDialog.vue';

defineOptions({
    layout: AdminMainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();
const toast = useToast();

const props = defineProps({
    order: Object,
    itemsWithContent: Array,
    allConfirmed: Boolean,
    hasContent: Boolean,
});

// Admin link input for suggested articles
const adminLinkInput = ref({});
const showAdminLinkModal = ref(false);
const selectedContentId = ref(null);
const showValidationErrorModal = ref(false);
const validationErrorMessage = ref('');
const validationWordCount = ref(0);
const showSimilarityConfirmation = ref(false);
const similarityData = ref(null);
const showRejectModal = ref(false);
const selectedRejectContentId = ref(null);
const rejectReason = ref('');
const rejectingContent = ref(false);

const showConfirmDialog = ref(false);
const confirmMessage = ref('');
const pendingConfirmContentId = ref(null);
const { showHistoryDialog, historyItem, openHistoryDialog, closeHistoryDialog } = useReviewHistory();

const isContentFullyApproved = (content) => content.confirmed_by_admin_at !== null;

const confirmContent = async (contentId) => {
    // Check if payment is confirmed
    if (!isPaymentConfirmed.value) {
        toast.add({
            severity: 'error',
            summary: $t('Payment Required'),
            detail: $t('Cannot confirm content until payment is confirmed or deferred payment is selected.'),
            life: 5000
        });
        return;
    }
    
    selectedContentId.value = contentId;
    
    // Get the content item from itemsWithContent
    const contentItem = props.itemsWithContent.find(item => item.content && item.content.id === contentId);
    
    if (contentItem && contentItem.content) {
        // Validate Google Docs if applicable
        if (contentItem.content.content_link && contentItem.content.auto_content === 0) {
            try {
                showLoading($t('Validating Google Docs content...'));
                
                const response = await axios.post('/google-docs/validate-content', {
                    google_docs_url: contentItem.content.content_link,
                    anchor_name: contentItem.content.anchor_name?.trim() || null,
                    anchor_url: contentItem.content.anchor_url?.trim() || null,
                    anchor_name_2: contentItem.content.anchor_name_2?.trim() || null,
                    anchor_url_2: contentItem.content.anchor_url_2?.trim() || null,
                    anchor_name_3: contentItem.content.anchor_name_3?.trim() || null,
                    anchor_url_3: contentItem.content.anchor_url_3?.trim() || null,
                });
                
                if (response.data.valid === 'similar') {
                    // Show similarity confirmation modal
                    hideLoading();
                    
                    similarityData.value = {
                        similarity: response.data.similarity,
                        expected: response.data.expected,
                        found: response.data.found,
                        url: response.data.url,
                        position: response.data.position || 1
                    };
                    showSimilarityConfirmation.value = true;
                    return;
                } else if (response.data.valid === false) {
                    hideLoading();
                    
                    // Show detailed error messages
                    let errorDetail = '';
                    if (response.data.errors && Array.isArray(response.data.errors)) {
                        errorDetail = response.data.errors.join('\n');
                    } else {
                        errorDetail = response.data.error || $t('Google Docs validation failed');
                    }
                    
                    toast.add({
                        severity: 'error',
                        summary: $t('Validation Error'),
                        detail: errorDetail,
                        life: 8000
                    });
                    return;
                }
            } catch (error) {
                hideLoading();
                
                if (error.response?.data?.valid === false) {
                    // Show detailed error messages from backend
                    let errorDetail = '';
                    if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
                        errorDetail = error.response.data.errors.join('\n');
                    } else {
                        errorDetail = error.response.data.error || $t('Google Docs validation failed');
                    }
                    
                    toast.add({
                        severity: 'error',
                        summary: $t('Validation Error'),
                        detail: errorDetail,
                        life: 8000
                    });
                    return;
                } else {
                    toast.add({
                        severity: 'warn',
                        summary: $t('Validation Warning'),
                        detail: $t('Could not validate Google Docs content. Proceeding with confirmation.'),
                        life: 3000
                    });
                }
            }
        }
    }
    
    openConfirmDialog(contentId);
};

const openAdminLinkModal = (contentId) => {
    selectedContentId.value = contentId;
    adminLinkInput.value[contentId] = '';
    showAdminLinkModal.value = true;
};

const closeAdminLinkModal = () => {
    showAdminLinkModal.value = false;
    selectedContentId.value = null;
    adminLinkInput.value = {};
};

const openValidationErrorModal = (errorMessage, wordCount = 0) => {
    validationErrorMessage.value = errorMessage;
    validationWordCount.value = wordCount;
    showValidationErrorModal.value = true;
};

const closeValidationErrorModal = () => {
    showValidationErrorModal.value = false;
    validationErrorMessage.value = '';
    validationWordCount.value = 0;
};

// Similarity confirmation functions
const confirmSimilarity = () => {
    showSimilarityConfirmation.value = false;
    similarityData.value = null;
    openConfirmDialog(selectedContentId.value);
};

const cancelSimilarity = () => {
    showSimilarityConfirmation.value = false;
    similarityData.value = null;
};

// Function to highlight differences between two texts
const highlightDifferences = (text1, text2) => {
    const words1 = text1.split(' ');
    const words2 = text2.split(' ');
    const maxLength = Math.max(words1.length, words2.length);
    
    let highlighted1 = '';
    let highlighted2 = '';
    
    for (let i = 0; i < maxLength; i++) {
        const word1 = words1[i] || '';
        const word2 = words2[i] || '';
        
        if (word1 !== word2) {
            highlighted1 += `<span class="bg-red-200 text-red-800 px-1 rounded">${word1}</span> `;
            highlighted2 += `<span class="bg-green-200 text-green-800 px-1 rounded">${word2}</span> `;
        } else {
            highlighted1 += word1 + ' ';
            highlighted2 += word2 + ' ';
        }
    }
    
    return {
        highlighted1: highlighted1.trim(),
        highlighted2: highlighted2.trim()
    };
};

// Proceed with confirmation (after confirm dialog)
const proceedWithConfirmation = (contentId, message = null) => {
    router.post(
        proxy.route ? proxy.route('order-content.confirm', contentId) : `/admin/order-content/${contentId}/confirm`,
        { message },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Confirming content...')),
            onFinish: () => hideLoading(),
            onError: (errors) => {
                hideLoading();
                if (errors.validation) {
                    const wordCountMatch = errors.validation.match(/(\d+)\s+words/);
                    if (wordCountMatch) {
                        const wordCount = parseInt(wordCountMatch[1]);
                        openValidationErrorModal(errors.validation, wordCount);
                        return;
                    }
                    
                    const anchorErrorCode = proxy.$page?.props?.flash?.anchor_error_code;
                    if (anchorErrorCode) {
                        const friendlyMessage = translateAnchorError(anchorErrorCode);
                        openValidationErrorModal(friendlyMessage, 0);
                        return;
                    }
                    
                    openValidationErrorModal(errors.validation, 0);
                }
            },
        }
    );
};

const submitAdminLink = () => {
    const contentId = selectedContentId.value;
    const link = adminLinkInput.value[contentId];
    
    if (!link || !link.trim()) {
        alert($t('Please enter a valid Google Docs link'));
        return;
    }
    
    router.post(
        proxy.route ? proxy.route('order-content.add-link', contentId) : `/admin/order-content/${contentId}/add-link`,
        {
            content_link: link.trim()
        },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Adding Google Docs link...')),
            onSuccess: () => {
                closeAdminLinkModal();
            },
            onFinish: () => hideLoading(),
            onError: () => hideLoading(),
        }
    );
};

const clearContentLink = (contentId) => {
    if (!confirm($t('Are you sure you want to clear this Google Docs link?'))) {
        return;
    }
    
    router.post(
        proxy.route ? proxy.route('order-content.clear-link', contentId) : `/admin/order-content/${contentId}/clear-link`,
        {},
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Clearing Google Docs link...')),
            onFinish: () => hideLoading(),
            onError: () => hideLoading(),
        }
    );
};

const downloadContent = (contentId) => {
    const url = proxy.route ? proxy.route('order-content.download', contentId) : `/admin/order-content/${contentId}/download`;
    window.open(url, '_blank');
};

const goToOrder = () => {
    router.visit(
        proxy.route ? proxy.route('orders.index') : `/admin/orders`,
        {
            onStart: () => showLoading($t('Loading order...')),
            onFinish: () => hideLoading(),
        }
    );
};

// Function to translate anchor validation error codes from backend
const translateAnchorError = (errorCode) => {

    if (errorCode === 'anchor_not_found') {
        return $t('Anchor tag not found in the document');
    }

    else if (errorCode === 'anchor_domain_mismatch') {
        return $t('Anchor name found but URL domain is incorrect. Please check if there\'s a typo in the URL');
    }

    else if (errorCode === 'anchor_name_similar') {
        return $t('Exact anchor text not found, but similar text was found in the document');
    } else if (errorCode === 'anchor_url_similar') {
        return $t('Exact anchor URL not found, but similar URL was found in the document');
    } else if (errorCode === 'anchor_both_similar') {
        return $t('Exact anchor tag not found, but similar text and URL were found in the document');
    }

    else if (errorCode === 'anchor_name_not_found') {
        return $t('Anchor text not found in the document');
    } else if (errorCode === 'anchor_url_not_found') {
        return $t('Anchor URL not found in the document');
    }

    else if (errorCode === 'access_denied') {
        return $t('Could not access document. Please ensure the document is publicly accessible or shared with view permissions');
    }

    else if (errorCode === 'invalid_url_format') {
        return $t('Invalid Google Docs URL format');
    }

    else if (errorCode === 'validation_exception') {
        return $t('Validation error occurred. Please try again');
    }
    
    return errorCode;
};

// Confirm dialog functions
const openConfirmDialog = (contentId) => {
    pendingConfirmContentId.value = contentId;
    confirmMessage.value = '';
    showConfirmDialog.value = true;
};

const closeConfirmDialog = () => {
    showConfirmDialog.value = false;
    pendingConfirmContentId.value = null;
    confirmMessage.value = '';
};

const submitConfirmDialog = () => {
    const contentId = pendingConfirmContentId.value;
    const message = confirmMessage.value.trim() || null;
    closeConfirmDialog();
    proceedWithConfirmation(contentId, message);
};

// Reject content functions
const openRejectModal = (contentId) => {
    selectedRejectContentId.value = contentId;
    rejectReason.value = '';
    showRejectModal.value = true;
};

const closeRejectModal = () => {
    showRejectModal.value = false;
    selectedRejectContentId.value = null;
    rejectReason.value = '';
};

const rejectContent = () => {
    if (!selectedRejectContentId.value) return;

    const contentId = selectedRejectContentId.value;
    const reason = rejectReason.value.trim() || null;
    closeRejectModal();

    router.post(
        proxy.route ? proxy.route('order-content.reject', contentId) : `/admin/order-content/${contentId}/reject`,
        { rejection_reason: reason },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => { rejectingContent.value = true; },
            onFinish: () => { rejectingContent.value = false; },
            onSuccess: () => {
                toast.add({
                    severity: 'success',
                    summary: $t('Success'),
                    detail: $t('Content rejected successfully. The client has been notified.'),
                    life: 5000
                });
            },
            onError: (errors) => {
                toast.add({
                    severity: 'error',
                    summary: $t('Error'),
                    detail: errors?.error || errors?.rejection_reason?.[0] || $t('Failed to reject content'),
                    life: 5000
                });
            },
        }
    );
};

// Computed property to check if payment is confirmed
const isPaymentConfirmed = computed(() => {
    const { payment_date, deferred_payment } = props.order;
    return payment_date || deferred_payment;
});

// Computed properties for metrics
const itemsCount = computed(() => props.itemsWithContent?.length || 0);
const contentCount = computed(() => props.itemsWithContent?.filter(item => item.content).length || 0);
const confirmedCount = computed(() => props.itemsWithContent?.filter(item => item.content?.confirmed_by_admin_at).length || 0);

const goToEditOrder = () => router.visit(proxy.route ? proxy.route('orders.edit', props.order.id) : `/admin/orders/${props.order.id}/edit`);
</script>

<template>
    <div class="grid">
        <!-- Header Card -->
        <Card class="mb-6">
            <template #header>
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between p-6 pb-0 gap-4">
                    <div>
                        <h1 class="text-2xl font-bold text-surface-900">
                            {{ $t('Content Review') }}
                        </h1>
                        <p class="text-surface-600 mt-1">
                            {{ $t('Order') }} #{{ order.number }} - {{ order.client?.name }}
                        </p>
                    </div>
                    <div class="flex items-center gap-3">
                        <Button
                            :label="$t('Edit order')"
                            icon="pi pi-pencil"
                            size="small"
                            outlined
                            @click="goToEditOrder"
                        />
                        <Button
                            :label="$t('Back to Orders')"
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            @click="goToOrder"
                        />
                    </div>
                </div>
            </template>

            <template #content>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                                <i class="pi pi-list text-white text-xl"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Total Items') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ itemsCount }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center">
                                <i class="pi pi-file text-white text-xl"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Content Submitted') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ contentCount }}/{{ itemsCount }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                <i class="pi pi-check text-white text-xl"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Confirmed') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ confirmedCount }}/{{ contentCount }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- Payment Warning Message -->
        <Card v-if="!isPaymentConfirmed" class="mb-6">
            <template #content>
                <div class="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div class="flex items-center gap-3 text-red-700">
                        <i class="pi pi-exclamation-triangle text-2xl"></i>
                        <div>
                            <p class="font-semibold text-lg">{{ $t('Payment Not Confirmed') }}</p>
                            <p class="text-sm">{{ $t('This order did not have the payment information provided. Confirmation of the content is blocked until payment is confirmed or the (Deferred Payment) option is selected.') }}</p>
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- No Content Message -->
        <Card v-if="!hasContent" class="mb-6">
            <template #content>
                <div class="text-center py-12">
                    <i class="pi pi-inbox text-6xl text-surface-300 mb-4"></i>
                    <h3 class="text-xl font-semibold text-surface-700 mb-2">
                        {{ $t('No content submitted yet') }}
                    </h3>
                    <p class="text-surface-500">
                        {{ $t('The seller has not submitted any content for this order yet.') }}
                    </p>
                </div>
            </template>
        </Card>

        <!-- Items List -->
        <div v-else class="space-y-4">
            <Card v-for="(item, index) in itemsWithContent" :key="item.id">
                <template #header>
                    <div class="p-6 pb-0">
                        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                            <div class="flex items-center gap-3">
                                <div class="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                                    <span class="font-bold text-primary-700">{{ index + 1 }}</span>
                                </div>
                                <div>
                                    <h3 class="font-semibold text-surface-900">
                                        {{ item.site_url || $t('Site URL not available') }}
                                    </h3>
                                    <p class="text-sm text-surface-600">
                                        {{ item.type_name || '-' }}
                                    </p>
                                </div>
                            </div>
                            <div class="flex items-center gap-2">
                                <Tag v-if="item.canceled_at" :value="$t('Canceled')" severity="danger" />
                                <Button
                                    v-if="item.content?.review_logs?.length"
                                    icon="pi pi-history"
                                    size="small"
                                    severity="secondary"
                                    text
                                    :label="$t('History')"
                                    @click="openHistoryDialog(item.content)"
                                />
                                <div v-if="item.content" class="flex gap-2 flex-wrap">
                                    <Tag
                                        v-if="isContentFullyApproved(item.content)"
                                        :value="$t('Confirmed')"
                                        severity="success"
                                        icon="pi pi-check"
                                    />
                                    <Tag
                                        v-else
                                        :value="$t('Pending Review')"
                                        severity="warn"
                                        icon="pi pi-clock"
                                    />
                                    <Tag
                                        v-if="item.content.skipped_validation"
                                        :value="$t('Sem Validação')"
                                        severity="warn"
                                        icon="pi pi-exclamation-triangle"
                                        v-tooltip.top="$t('Conteúdo salvo sem validação de âncoras')"
                                    />
                                </div>
                                <div v-else>
                                    <Tag
                                        :value="$t('No Content')"
                                        severity="secondary"
                                        icon="pi pi-minus"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <template #content>
                    <!-- Site Features Information -->
                    <SiteFeaturesInfo v-if="item.site_features" :features="item.site_features" />

                    <div v-if="item.content" class="space-y-4">
                        <!-- Anchors Display -->
                        <div v-if="item.content.anchor_name || item.content.anchor_url || item.content.anchor_name_2 || item.content.anchor_url_2 || item.content.anchor_name_3 || item.content.anchor_url_3" 
                             class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h5 class="text-sm font-semibold text-blue-900 mb-3">
                                <i class="pi pi-link text-blue-700 mr-2"></i>
                                {{ $t('Anchors') }}
                            </h5>
                            
                            <div class="flex flex-col md:flex-row gap-4">
                                <!-- Anchor 1 -->
                                <div v-if="item.content.anchor_name || item.content.anchor_url" class="flex-1">
                                    <div class="space-y-2">
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor Name') }} 1
                                            </label>
                                            <p class="text-sm text-blue-900">{{ item.content.anchor_name || '-' }}</p>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor URL') }} 1
                                            </label>
                                            <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url || '-' }}</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Divider -->
                                <Divider v-if="(item.content.anchor_name || item.content.anchor_url) && (item.content.anchor_name_2 || item.content.anchor_url_2)" layout="vertical" class="hidden md:flex" />
                                
                                <!-- Anchor 2 -->
                                <div v-if="item.content.anchor_name_2 || item.content.anchor_url_2" class="flex-1">
                                    <div class="space-y-2">
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor Name') }} 2
                                            </label>
                                            <p class="text-sm text-blue-900">{{ item.content.anchor_name_2 || '-' }}</p>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor URL') }} 2
                                            </label>
                                            <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url_2 || '-' }}</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Divider -->
                                <Divider v-if="(item.content.anchor_name_2 || item.content.anchor_url_2) && (item.content.anchor_name_3 || item.content.anchor_url_3)" layout="vertical" class="hidden md:flex" />
                                
                                <!-- Anchor 3 -->
                                <div v-if="item.content.anchor_name_3 || item.content.anchor_url_3" class="flex-1">
                                    <div class="space-y-2">
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor Name') }} 3
                                            </label>
                                            <p class="text-sm text-blue-900">{{ item.content.anchor_name_3 || '-' }}</p>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor URL') }} 3
                                            </label>
                                            <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url_3 || '-' }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Other Content Details -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-medium text-surface-700 mb-1">
                                    {{ $t('Language') }}
                                </label>
                                <p class="text-surface-900">{{ item.content.language || '-' }}</p>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-surface-700 mb-1">
                                    {{ $t('Submitted At') }}
                                </label>
                                <p class="text-surface-900">
                                    {{ item.content.submited_at ? new Date(item.content.submited_at).toLocaleString() : '-' }}
                                </p>
                            </div>
                        </div>

        <!-- Content Link or File -->
        <div v-if="item.content.auto_content === 1" class="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <label class="block text-sm font-medium text-blue-900 mb-2">
                <i class="pi pi-lightbulb text-blue-600 mr-1"></i>
                {{ $t('Suggested Article Requested') }}
            </label>
            <p class="text-blue-800 text-sm mb-3">
                {{ $t('Client requested a 500-word article suggestion') }}
            </p>                            <div v-if="item.content.content_link" class="flex items-center justify-between">
                                <div class="flex items-center gap-2">
                                    <i class="pi pi-link text-primary-500"></i>
                                    <a
                                        :href="item.content.content_link"
                                        target="_blank"
                                        class="text-primary-500 hover:text-primary-600 underline break-all"
                                    >
                                        {{ item.content.content_link }}
                                    </a>
                                </div>
                                <div class="flex gap-2 ml-4">
                                    <Button
                                        :label="$t('Edit')"
                                        icon="pi pi-pencil"
                                        size="small"
                                        severity="secondary"
                                        :disabled="isContentFullyApproved(item.content)"
                                        @click="openAdminLinkModal(item.content.id)"
                                    />
                                    <Button
                                        :label="$t('Clear')"
                                        icon="pi pi-trash"
                                        size="small"
                                        severity="danger"
                                        :disabled="isContentFullyApproved(item.content)"
                                        @click="clearContentLink(item.content.id)"
                                    />
                                </div>
                            </div>
                            <div v-else class="flex justify-start">
                                <Button
                                    :label="$t('Add Google Docs Link')"
                                    icon="pi pi-plus"
                                    size="small"
                                    severity="info"
                                    :disabled="isContentFullyApproved(item.content)"
                                    @click="openAdminLinkModal(item.content.id)"
                                />
                            </div>
                        </div>
                        
                        <div v-else-if="item.content.content_link || item.content.content_link_file" class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Content') }}
                            </label>

                            <div v-if="item.content.content_link" class="flex items-center gap-2">
                                <i class="pi pi-link text-primary-500"></i>
                                <a
                                    :href="item.content.content_link"
                                    target="_blank"
                                    class="text-primary-500 hover:text-primary-600 underline break-all"
                                >
                                    {{ item.content.content_link }}
                                </a>
                            </div>

                            <div v-if="item.content.content_link_file" class="flex items-center gap-2">
                                <i class="pi pi-file text-primary-500"></i>
                                <Button
                                    :label="$t('Download File')"
                                    icon="pi pi-download"
                                    size="small"
                                    @click="downloadContent(item.content.id)"
                                />
                            </div>
                        </div>

                        <!-- Observations -->
                        <div v-if="item.observations" class="p-4 bg-amber-50 rounded-lg border border-amber-200">
                            <label class="block text-sm font-medium text-amber-900 mb-2">
                                <i class="pi pi-info-circle mr-1"></i>
                                {{ $t('Observations') }}
                            </label>
                            <p class="text-surface-900">{{ item.observations }}</p>
                        </div>

                        <!-- Confirmation Status -->
                        <div v-if="isContentFullyApproved(item.content)" class="p-4 bg-green-50 rounded-lg border border-green-200">
                            <div class="flex items-center gap-2 text-green-700">
                                <i class="pi pi-check-circle text-xl"></i>
                                <div>
                                    <p class="font-medium">{{ $t('Content confirmed') }}</p>
                                    <p class="text-sm">
                                        {{ new Date(item.content.confirmed_by_admin_at).toLocaleString() }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Rejection Status -->
                        <div v-else-if="item.content.rejected_at" class="p-4 bg-red-50 rounded-lg border border-red-200">
                            <div class="flex items-center gap-2 text-red-700 mb-3">
                                <i class="pi pi-times-circle text-xl"></i>
                                <div>
                                    <p class="font-medium">{{ $t('Content rejected') }}</p>
                                    <p class="text-sm">
                                        {{ new Date(item.content.rejected_at).toLocaleString() }}
                                    </p>
                                </div>
                            </div>
                            
                            <div v-if="item.content.rejection_reason" class="mb-3">
                                <label class="block text-sm font-medium text-red-900 mb-1">
                                    {{ $t('Rejection reason') }}:
                                </label>
                                <p class="text-sm text-red-800">{{ item.content.rejection_reason }}</p>
                            </div>
                            
                            <div class="p-3 bg-amber-50 rounded-lg border border-amber-200">
                                <div class="flex items-center gap-2 text-amber-700">
                                    <i class="pi pi-clock text-lg"></i>
                                    <p class="text-sm font-medium">{{ $t('Waiting for client to revise and resubmit content') }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- Action Buttons -->
                        <div v-else-if="(item.content.auto_content === 1 && item.content.content_link) || (item.content.auto_content === 0 && (item.content.content_link || item.content.content_link_file))" class="flex justify-end gap-3">
                            <Button
                                :label="$t('Reject')"
                                icon="pi pi-times"
                                severity="danger"
                                outlined
                                :disabled="!!item.content.sent_to_portal_at"
                                @click="openRejectModal(item.content.id)"
                            />
                            <Button
                                :label="$t('Confirm Content')"
                                icon="pi pi-check"
                                severity="success"
                                :disabled="(!!item.content.rejected_at && !item.content.revised_at) || !isPaymentConfirmed"
                                @click="confirmContent(item.content.id)"
                            />
                        </div>
                    </div>

                    <div v-else class="text-center py-6 text-surface-500">
                        <i class="pi pi-inbox text-4xl mb-2"></i>
                        <p>{{ $t('No content submitted for this item yet') }}</p>
                    </div>
                </template>
            </Card>
        </div>

        <!-- All Confirmed Message -->
        <Card v-if="allConfirmed && hasContent" class="mt-6">
            <template #content>
                <div class="text-center py-8">
                    <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <i class="pi pi-check text-green-600 text-3xl"></i>
                    </div>
                    <h3 class="text-xl font-semibold text-surface-900 mb-2">
                        {{ $t('All content confirmed!') }}
                    </h3>
                    <p class="text-surface-600">
                        {{ $t('All submitted content has been reviewed and confirmed. The order has moved to submission.') }}
                    </p>
                </div>
            </template>
        </Card>
    </div>

    <!-- Admin Link Modal -->
    <Dialog
        v-model:visible="showAdminLinkModal"
        :header="$t('Add Google Docs Link')"
        :modal="true"
        :closable="true"
        :style="{ width: '50vw' }"
        :breakpoints="{ '960px': '75vw', '640px': '95vw' }"
        @hide="closeAdminLinkModal"
    >
        <div class="space-y-4">
            <div>
                <label for="admin_link" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Google Docs Link') }} <span class="text-red-500">*</span>
                </label>
                <InputText
                    id="admin_link"
                    v-model="adminLinkInput[selectedContentId]"
                    class="w-full"
                    type="url"
                    :placeholder="$t('Enter Google Docs link for the 500-word article')"
                />
                <small class="text-surface-500 block mt-1">
                    {{ $t('Paste the shareable link from Google Docs containing the 500-word article') }}
                </small>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end">
                <div class="flex gap-2">
                    <Button
                        :label="$t('Cancel')"
                        severity="secondary"
                        outlined
                        @click="closeAdminLinkModal"
                    />
                    <Button
                        :label="$t('Add Link')"
                        icon="pi pi-check"
                        @click="submitAdminLink"
                    />
                </div>
            </div>
        </template>
    </Dialog>

    <Dialog
        v-model:visible="showValidationErrorModal"
        :header="validationWordCount > 0 ? $t('Document Validation Error') : $t('Content Validation Error')"
        :modal="true"
        :closable="true"
        :style="{ width: '30vw' }"
        :breakpoints="{ '960px': '75vw', '640px': '95vw' }"
        @hide="closeValidationErrorModal"
    >
        <div class="space-y-4">
            <div class="flex items-start gap-3">
                <div class="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <i class="pi pi-exclamation-triangle text-red-600 text-xl"></i>
                </div>
                <div class="flex-1">
                    <h3 v-if="validationWordCount > 0" class="text-lg font-semibold text-surface-900 mb-2">
                        {{ $t('Document exceeds word limit') }}
                    </h3>
                    <h3 v-else class="text-lg font-semibold text-surface-900 mb-2">
                        {{ $t('Content validation failed') }}
                    </h3>
                    <p class="text-surface-700">
                        {{ validationErrorMessage }}
                    </p>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end">
                <Button
                    :label="$t('Close')"
                    icon="pi pi-times"
                    severity="secondary"
                    outlined
                    @click="closeValidationErrorModal"
                />
            </div>
        </template>
    </Dialog>

    <!-- Similarity Confirmation Modal -->
    <Dialog
        v-model:visible="showSimilarityConfirmation"
        modal
        :closable="false"
        :header="$t('Similarity Confirmation')"
        class="w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2"
    >
        <div v-if="similarityData" class="space-y-6">            
            <div class="grid md:grid-cols-2 gap-4">
                <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 class="font-semibold text-red-800 mb-2">{{ $t('Expected (Your Anchor)') }}:</h4>
                    <div 
                        class="text-sm text-red-700 break-words"
                        v-html="highlightDifferences(similarityData.expected, similarityData.found).highlighted1"
                    ></div>
                </div>
                
                <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 class="font-semibold text-green-800 mb-2">{{ $t('Found in Document') }}:</h4>
                    <div 
                        class="text-sm text-green-700 break-words"
                        v-html="highlightDifferences(similarityData.expected, similarityData.found).highlighted2"
                    ></div>
                </div>
            </div>
            
            <div class="text-center text-sm text-surface-600">
                <p class="text-xs">
                    <strong>{{ $t('Continue') }}:</strong> {{ $t('Accepts the difference and confirms the content') }}
                    <br>
                    <strong>{{ $t('Cancel') }}:</strong> {{ $t('Returns to review') }}
                </p>
            </div>
        </div>
        
        <template #footer>
            <div class="flex justify-center space-x-4">
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    severity="secondary"
                    @click="cancelSimilarity"
                />
                <Button
                    :label="$t('Continue Anyway')"
                    icon="pi pi-check"
                    severity="warning"
                    @click="confirmSimilarity"
                />
            </div>
        </template>
    </Dialog>

    <!-- Reject Content Modal -->
    <Dialog
        v-model:visible="showRejectModal"
        modal
        :header="$t('Reject Content')"
        :style="{ width: '500px' }"
        @hide="closeRejectModal"
    >
        <div class="space-y-4">
            <p class="text-surface-700">
                {{ $t('Please provide feedback for the content rejection. This will help the client understand what needs to be revised.') }}
            </p>
            
            <div class="space-y-2">
                <label for="rejectReason" class="block text-sm font-medium text-surface-700">
                    {{ $t('Rejection Reason (Optional)') }}
                </label>
                <Textarea
                    id="rejectReason"
                    v-model="rejectReason"
                    rows="4"
                    class="w-full"
                    :placeholder="$t('Describe what needs to be changed or improved...')"
                />
                <small class="text-surface-600">
                    {{ $t('You can reject content without providing a reason, but feedback helps improve the revision process.') }}
                </small>
            </div>
        </div>
        
        <template #footer>
            <div class="flex justify-end space-x-3">
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    severity="secondary"
                    @click="closeRejectModal"
                    :disabled="rejectingContent"
                />
                <Button
                    :label="$t('Reject Content')"
                    icon="pi pi-times"
                    severity="danger"
                    @click="rejectContent"
                    :loading="rejectingContent"
                />
            </div>
        </template>
    </Dialog>

    <!-- Confirm Content Dialog -->
    <Dialog
        v-model:visible="showConfirmDialog"
        modal
        :header="$t('Confirm Content')"
        :style="{ width: '500px' }"
        :breakpoints="{ '640px': '95vw' }"
        @hide="closeConfirmDialog"
    >
        <div class="space-y-4">
            <div class="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <i class="pi pi-check-circle text-green-600 text-lg mt-0.5"></i>
                <div>
                    <p class="font-medium text-green-800 mb-1">{{ $t('Approve this content?') }}</p>
                    <p class="text-sm text-green-600">{{ $t('The client will be notified that the content was approved.') }}</p>
                </div>
            </div>
            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Message (Optional)') }}
                </label>
                <Textarea
                    v-model="confirmMessage"
                    rows="3"
                    class="w-full"
                    :placeholder="$t('Add a message for the client (optional)...')"
                />
            </div>
        </div>
        <template #footer>
            <div class="flex justify-end gap-2">
                <Button
                    :label="$t('Cancel')"
                    severity="secondary"
                    outlined
                    @click="closeConfirmDialog"
                />
                <Button
                    :label="$t('Confirm Content')"
                    icon="pi pi-check"
                    severity="success"
                    @click="submitConfirmDialog"
                />
            </div>
        </template>
    </Dialog>

    <ReviewHistoryDialog :visible="showHistoryDialog" :item="historyItem" @hide="closeHistoryDialog" />
</template>

<style scoped>
.p-tag {
    font-weight: 600;
}
</style>
